import React, { useRef, useState } from 'react';
import { DialogActions, DialogContent, Menu, MenuItem, ListItemIcon } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import HistoryIcon from '@mui/icons-material/History';
import ChatIcon from '@mui/icons-material/Chat';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import EditIcon from '@mui/icons-material/Edit';
import LoginIcon from '@mui/icons-material/Login';
import StarsIcon from '@mui/icons-material/Stars';
import { useDispatch } from 'react-redux';

import { NotificationTypeEnum, UserKindEnum } from '../../../../types/enums';
import { adminProvider } from '@/providers';
import { appSetNotification } from '@/store';
import { MyButton } from '../../../uiComponents/MyButton/MyButton';
import { MyDialog } from '../../../uiComponents/MyDialog/MyDialog';
import { getPublicationTimerResetResult } from '../../publicationTimerResetMessages';

interface UserMenuProps {
  anchorEl?: Element;
  anchorPosition: { top: number; left: number } | undefined;
  chatId?: string;
  isBlocked?: boolean;
  isVerificationEnabled?: boolean;
  isAmbassador?: boolean;
  userKind?: string;
  userId?: string;
  handleMenuClose: () => void;
  handleBlockUserDialog: () => void;
  handleUnBlockUserDialog: () => void;
  handleBlockHistoryDialog: () => void;
  handleImpersonateUserDialog: () => void;
  handleEmploymentRequestDialog: () => void;
  handleVerifyUserDialog: () => void;
  handleAmbassadorStatusChange: (isAmbassador: boolean) => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({
  anchorEl,
  anchorPosition,
  isBlocked,
  isVerificationEnabled,
  isAmbassador,
  userKind,
  userId,
  chatId,
  handleMenuClose,
  handleBlockUserDialog,
  handleUnBlockUserDialog,
  handleBlockHistoryDialog,
  handleImpersonateUserDialog,
  handleEmploymentRequestDialog,
  handleVerifyUserDialog,
  handleAmbassadorStatusChange,
}) => {
  const dispatch = useDispatch();
  const requestInFlightRef = useRef(false);
  const [isExtendDialogOpen, setIsExtendDialogOpen] = useState(false);
  const [isExtending, setIsExtending] = useState(false);
  const [updatedCount, setUpdatedCount] = useState<number | null>(null);
  const canSetAmbassador = userKind === UserKindEnum.Broker || userKind === UserKindEnum.Manager;

  const handleBlockUser = () => {
    handleBlockUserDialog();
    handleMenuClose();
  };

  const handleUnBlockUser = () => {
    handleUnBlockUserDialog();
    handleMenuClose();
  };

  const handleBlockHistory = () => {
    handleBlockHistoryDialog();
    handleMenuClose();
  };

  const handleChatWithUser = () => {
    handleMenuClose();
    location.href = `/chat/group_${chatId}`;
  };

  const handleVerifyUser = () => {
    handleVerifyUserDialog();
    handleMenuClose();
  };

  const handleEditUserProfile = () => {
    handleMenuClose();
  };

  const handleLoginAsUser = () => {
    handleMenuClose();
    handleImpersonateUserDialog();
  };

  const handleEmploymentRequest = () => {
    handleMenuClose();
    handleEmploymentRequestDialog();
  };

  const handleAmbassadorChange = () => {
    handleAmbassadorStatusChange(!isAmbassador);
    handleMenuClose();
  };

  const handleOpenExtendDialog = () => {
    setUpdatedCount(null);
    setIsExtendDialogOpen(true);
    handleMenuClose();
  };

  const handleCloseExtendDialog = () => {
    if (!requestInFlightRef.current) {
      setIsExtendDialogOpen(false);
      setUpdatedCount(null);
    }
  };

  const handleExtendPublishedListings = async () => {
    if (!userId || requestInFlightRef.current) return;

    requestInFlightRef.current = true;
    setIsExtending(true);

    try {
      const { data, message, ok, status } =
        await adminProvider.resetUserPropertyAdPublicationTimer(userId);

      if (!ok || !data) {
        dispatch(
          appSetNotification(
            NotificationTypeEnum.Error,
            message || 'Unable to extend published listings',
            status
          )
        );
        return;
      }

      setUpdatedCount(data.updated);
    } catch (error) {
      dispatch(
        appSetNotification(
          NotificationTypeEnum.Error,
          error instanceof Error ? error.message : 'Unable to extend published listings'
        )
      );
    } finally {
      requestInFlightRef.current = false;
      setIsExtending(false);
    }
  };

  const resetResult =
    updatedCount === null ? null : getPublicationTimerResetResult(updatedCount, 'user');

  const menu = (
    <Menu
      id='user-menu'
      anchorEl={anchorEl}
      anchorReference='anchorPosition'
      anchorPosition={anchorPosition}
      open={Boolean(anchorEl)}
      onClose={handleMenuClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      {isBlocked && (
        <MenuItem onClick={handleUnBlockUser}>
          <ListItemIcon>
            <BlockIcon />
          </ListItemIcon>
          UnBlock user
        </MenuItem>
      )}
      {!isBlocked && (
        <MenuItem onClick={handleBlockUser}>
          <ListItemIcon>
            <BlockIcon />
          </ListItemIcon>
          Block user
        </MenuItem>
      )}
      <MenuItem onClick={handleBlockHistory}>
        <ListItemIcon>
          <HistoryIcon />
        </ListItemIcon>
        Block history
      </MenuItem>
      <MenuItem onClick={handleChatWithUser}>
        <ListItemIcon>
          <ChatIcon />
        </ListItemIcon>
        Chat
      </MenuItem>
      <MenuItem onClick={handleVerifyUser} disabled={!isVerificationEnabled}>
        <ListItemIcon>
          <VerifiedUserIcon />
        </ListItemIcon>
        Verification
      </MenuItem>
      {userKind !== UserKindEnum.Admin && (isAmbassador || canSetAmbassador) && (
        <MenuItem onClick={handleAmbassadorChange}>
          <ListItemIcon>
            <StarsIcon />
          </ListItemIcon>
          {isAmbassador ? 'Remove ambassador' : 'Set as ambassador'}
        </MenuItem>
      )}
      {userKind !== undefined && userKind !== UserKindEnum.Admin && userId && (
        <MenuItem onClick={handleOpenExtendDialog}>
          <ListItemIcon>
            <HistoryIcon />
          </ListItemIcon>
          Extend published listings
        </MenuItem>
      )}
      <MenuItem onClick={handleEditUserProfile}>
        <ListItemIcon>
          <EditIcon />
        </ListItemIcon>
        Edit user profile
      </MenuItem>
      <MenuItem onClick={handleLoginAsUser}>
        <ListItemIcon>
          <LoginIcon />
        </ListItemIcon>
        Login as user
      </MenuItem>
      <MenuItem onClick={handleEmploymentRequest}>
        <ListItemIcon>
          <LoginIcon />
        </ListItemIcon>
        Employment request
      </MenuItem>
    </Menu>
  );

  return (
    <>
      {menu}
      <MyDialog
        dialogTitle={resetResult?.title || 'Extend published listings?'}
        onClose={handleCloseExtendDialog}
        open={isExtendDialogOpen}
        width='600'
      >
        <DialogContent sx={{ paddingX: 0, textAlign: 'center' }}>
          {resetResult?.text ||
            'All currently published listings for this user will receive a new 90-day publication period.'}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 2, justifyContent: 'center', paddingX: 0 }}>
          {resetResult ? (
            <MyButton
              data={{
                buttonName: 'Done',
                buttonType: 'button',
                customWidth: '190px',
                variant: 'contained',
              }}
              onClick={handleCloseExtendDialog}
            />
          ) : (
            <>
              <MyButton
                data={{
                  buttonName: 'Cancel',
                  buttonType: 'button',
                  customWidth: '190px',
                  styleType: 'cancel',
                  variant: 'outlined',
                }}
                disabled={isExtending}
                onClick={handleCloseExtendDialog}
              />
              <MyButton
                data={{
                  buttonName: isExtending ? 'Extending...' : 'Extend by 90 days',
                  buttonType: 'button',
                  customWidth: '190px',
                  variant: 'contained',
                }}
                disabled={isExtending}
                onClick={handleExtendPublishedListings}
              />
            </>
          )}
        </DialogActions>
      </MyDialog>
    </>
  );
};
